import { useState, useRef, useEffect } from 'react';

interface AutocompleteOption {
  value: string;
  label: string;
  extra?: string; // Shown as secondary text (e.g. province)
}

interface AutocompleteProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSelect: (option: AutocompleteOption) => void;
  options: AutocompleteOption[];
  error?: string;
  helpText?: string;
  required?: boolean;
  placeholder?: string;
  maxResults?: number;
}

/**
 * Autocomplete input with dropdown suggestions.
 * Filters options as user types, supports keyboard navigation.
 */
export function Autocomplete({
  label,
  value,
  onChange,
  onSelect,
  options,
  error,
  helpText,
  required,
  placeholder,
  maxResults = 8,
}: AutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = value.length >= 2
    ? options.filter((opt) =>
        opt.label.toLowerCase().startsWith(value.toLowerCase())
      )
      .concat(
        options.filter((opt) =>
          !opt.label.toLowerCase().startsWith(value.toLowerCase()) &&
          opt.label.toLowerCase().includes(value.toLowerCase())
        )
      )
      .slice(0, maxResults)
    : [];

  const showDropdown = isOpen && filtered.length > 0;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const handleSelect = (option: AutocompleteOption) => {
    onSelect(option);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const inputId = `autocomplete-${label.toLowerCase().replace(/\s/g, '-')}`;
  const errorId = `${inputId}-error`;
  const helpId = `${inputId}-help`;

  return (
    <div className={`form-group autocomplete-wrapper ${error ? 'form-group-error' : ''}`} ref={wrapperRef}>
      <label htmlFor={inputId}>{label}{required && ' *'}</label>
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setIsOpen(true); setHighlightedIndex(-1); }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={`${inputId}-listbox`}
        aria-activedescendant={highlightedIndex >= 0 ? `${inputId}-option-${highlightedIndex}` : undefined}
        aria-invalid={!!error}
        aria-describedby={[error ? errorId : '', helpText ? helpId : ''].filter(Boolean).join(' ') || undefined}
      />
      {showDropdown && (
        <ul className="autocomplete-dropdown" id={`${inputId}-listbox`} role="listbox">
          {filtered.map((option, index) => (
            <li
              key={option.value}
              id={`${inputId}-option-${index}`}
              role="option"
              aria-selected={index === highlightedIndex}
              className={index === highlightedIndex ? 'autocomplete-option highlighted' : 'autocomplete-option'}
              onMouseDown={() => handleSelect(option)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span className="autocomplete-option-label">{option.label}</span>
              {option.extra && <span className="autocomplete-option-extra">{option.extra}</span>}
            </li>
          ))}
        </ul>
      )}
      {helpText && !error && <small id={helpId}>{helpText}</small>}
      {error && <small id={errorId} className="form-error" role="alert">{error}</small>}
    </div>
  );
}
